import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

// Copy Requirement Dialog Component
const CopyRequirementDialog = ({
  isOpen,
  onClose,
  requirementToCopy,
  corporateAccount,
  currentProject,
  login_token
}) => {
  const [functionalLevels, setFunctionalLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Created'); // Default status
  const [copyAttachments, setCopyAttachments] = useState(false); // New state for attachment option
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(null);
  const [attachmentsCount, setAttachmentsCount] = useState(0); // Track number of attachments
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false); // Track attachment loading state

  // Add refs for focus management
  const levelSelectRef = useRef(null);

  // Styles from the parent component
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    dialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      width: '700px', // Changed from 700px to match other dialogs
      maxWidth: '90vw',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      maxHeight: '150vh',
      overflowY: 'auto'
    }
  };

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && levelSelectRef.current) {
      levelSelectRef.current.focus();
    }
  }, [isOpen]);

  // Fetch functional levels and other data when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchFunctionalLevels();
      fetchStatuses();

      // Set the default target level to the same as source if available
      if (requirementToCopy && requirementToCopy.level_id) {
        setSelectedLevelId(requirementToCopy.level_id.toString());
      }

      // Only try to fetch attachments if we have a valid requirement to copy
      if (requirementToCopy && requirementToCopy.req_id) {
        fetchAttachmentsCount();
      }
    } else {
      // Reset states when dialog closes
      setSelectedLevelId('');
      setSelectedStatus('Created');
      setCopyAttachments(false);
      setAttachmentsCount(0);
      setError('');
      setCopySuccess(null);
    }
  }, [isOpen, corporateAccount, currentProject, requirementToCopy]);

  // New function to fetch attachments count
  const fetchAttachmentsCount = async () => {
    if (!requirementToCopy || !requirementToCopy.req_id_with_prefix) return;

    setIsLoadingAttachments(true);

    try {
      // Log debugging info
      console.log("Fetching attachments for:", {
        corporateAccount,
        projectId: currentProject,
        reqId: requirementToCopy.req_id_with_prefix
      });

      const response = await fetch(appConfig.getUrl('/get_requirement_attachments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          req_id: requirementToCopy.req_id_with_prefix
        })
      });

      const data = await response.json();
      console.log("Attachment response:", data);

      if (data.status === 'Success' && data.attachments) {
        setAttachmentsCount(data.attachments.length);
      } else {
        console.warn("No attachments found or unexpected response format:", data);
        setAttachmentsCount(0);
      }
    } catch (error) {
      console.error('Error fetching attachments count:', error);
      setAttachmentsCount(0);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const fetchFunctionalLevels = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(appConfig.getUrl('/get_functional_levels'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          search_query: "",
          parent_level_id: null,
          traverse_to_lowest_level: true
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        // Sort functional levels alphabetically by hierarchy name
        const sortedLevels = [...data.functional_level_list].sort((a, b) =>
          a.functional_level_hierarchy.localeCompare(b.functional_level_hierarchy)
        );
        setFunctionalLevels(sortedLevels);
      } else {
        setError(data.status_description || 'Failed to load functional levels');
      }
    } catch (error) {
      setError('Error fetching functional levels: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatuses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setAvailableStatuses(statuses);

        // Set default status to 'Created' if available, otherwise use first status
        if (statuses.includes('Created')) {
          setSelectedStatus('Created');
        } else if (statuses.length > 0) {
          setSelectedStatus(statuses[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      setError('Error loading statuses: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedLevelId) {
      setError('Please select a target functional level');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Log request details for debugging
      console.log("Sending copy requirement request:", {
        corporateAccount,
        fromProjectId: currentProject,
        fromReqId: requirementToCopy.req_id,
        toProjectId: currentProject,
        toLevelId: selectedLevelId,
        toStatus: selectedStatus,
        copyAttachments
      });

      const response = await fetch(appConfig.getUrl('/copy_requirement'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          from_project_id: currentProject,
          from_req_id: requirementToCopy.req_id,
          to_project_id: currentProject, // Same project for now
          to_level_id: selectedLevelId,
          to_status: selectedStatus, // Use the selected status
          copy_attachments: copyAttachments // New parameter to control attachment copying
        })
      });

      const data = await response.json();
      console.log('Copy response:', data);

      if (data.status === 'Success') {
        setError('Requirement copied successfully');

        // Delay the success state to show success message
        setTimeout(() => {
          setCopySuccess(data);
        }, 2000);
      } else {
        setError(data.status_description || 'Failed to copy requirement');
      }
    } catch (error) {
      console.error('Error copying requirement:', error);
      setError('Error copying requirement: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Copy Requirement
        </h2>

        {copySuccess ? (
          <div className="mb-4">
            <div style={{
              marginBottom: '15px',
              padding: '12px',
              borderRadius: '4px',
              backgroundColor: '#f0f9ff',
              color: '#059669',
              border: '1px solid #10b981'
            }}>
              <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Requirement successfully copied!</p>
              <p style={{ marginBottom: '4px' }}>New Requirement ID: <span style={{ fontWeight: 'bold' }}>{copySuccess.req_id}</span></p>
              {copyAttachments && attachmentsCount > 0 && (
                <p style={{ margin: '0' }}>
                  {attachmentsCount} attachment{attachmentsCount !== 1 ? 's' : ''} copied
                </p>
              )}
            </div>
            <button
              style={{
                width: '100%',
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => onClose(true)}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handleCopy(); }}>
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                You are about to copy the requirement <strong>{requirementToCopy?.req_id_with_prefix}</strong>: {requirementToCopy?.req_description}
              </p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Target Project: <span style={{ color: '#dc2626', fontSize: '12px' }}>Currently, copying is only supported within the same project</span>
                </label>
                <input
                  type="text"
                  value={currentProject}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                  disabled
                />
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px', marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Source Functional Level:
                </label>
                <input
                  type="text"
                  value={requirementToCopy?.level_description || 'Not available'}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                  disabled
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Target Functional Level: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  ref={levelSelectRef}
                  value={selectedLevelId}
                  onChange={(e) => setSelectedLevelId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={isLoading}
                  required
                >
                  <option value="">Select a functional level</option>
                  {functionalLevels.map(level => (
                    <option key={level.level_id} value={level.level_id}>
                      {level.functional_level_hierarchy}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Target Status: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={isLoading}
                  required
                >
                  {availableStatuses.length === 0 ? (
                    <option value="Created">Created</option>
                  ) : (
                    availableStatuses.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* New checkbox for copying attachments */}
              {isLoadingAttachments ? (
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #3b82f6',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px'
                  }}></div>
                  <span style={{ fontSize: '14px', color: '#666' }}>Checking attachments...</span>
                </div>
              ) : attachmentsCount > 0 ? (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={copyAttachments}
                      onChange={(e) => setCopyAttachments(e.target.checked)}
                      style={{ marginRight: '8px' }}
                      disabled={isLoading}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Copy attachments ({attachmentsCount} attachment{attachmentsCount !== 1 ? 's' : ''})
                    </span>
                  </label>
                </div>
              ) : null}
            </div>

            {error && (
              <div style={{
                marginBottom: '15px',
                padding: '8px',
                borderRadius: '4px',
                backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
                color: error.includes('successfully') ? '#059669' : '#dc2626',
                border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button
                type="button"
                onClick={() => onClose(false)}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                disabled={isLoading}
              >
                Close
              </button>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#C8F4DE',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  opacity: (isLoading || !selectedLevelId) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
                disabled={isLoading || !selectedLevelId}
              >
                {isLoading && (
                  <svg style={{
                    animation: 'spin 1s linear infinite',
                    marginLeft: '-4px',
                    marginRight: '8px',
                    width: '16px',
                    height: '16px'
                  }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Processing...' : 'Copy Requirement'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CopyRequirementDialog;